import { useEffect, useRef, useState } from 'react'

import { Autocomplete, TextField } from '@mui/material'

import { CustomFieldOption } from '../../../../../../../types'
import { INITIAL_FIELD_KEY } from '../../../constants'

interface AddableSelectProps {
  label?: string
  initialOptions: CustomFieldOption[]
  value?: string
  onChange?: (newValue: string) => void
  onBlur?: () => void
  selectedValues: string[]
  error?: boolean
  errorText?: string
  maxLength?: number
  maxLengthErrorText?: string
  autoFocus?: boolean
}

export default function FreeSelect({
  label,
  initialOptions,
  value: externalValue,
  onChange,
  onBlur,
  selectedValues,
  error,
  errorText,
  maxLength,
  maxLengthErrorText,
  autoFocus = false,
}: AddableSelectProps) {
  const [value, setValue] = useState<CustomFieldOption | null>(
    externalValue ? { label: externalValue, value: externalValue } : null,
  )
  const [inputValue, setInputValue] = useState<string>(externalValue || INITIAL_FIELD_KEY)
  const selectionMadeRef = useRef(false)

  // Show a length warning as soon as the typed (not-yet-committed) text goes over the limit,
  const isTooLong = !!maxLength && inputValue.length > maxLength

  // Sync internal state with external value prop
  useEffect(() => {
    if (externalValue) {
      setValue({ label: externalValue, value: externalValue })
      setInputValue(externalValue)
    } else {
      setValue(null)
      setInputValue(INITIAL_FIELD_KEY)
    }
  }, [externalValue])

  // Generate dynamic options based on current input
  const displayOptions =
    inputValue.trim() &&
    !initialOptions.some(opt => opt.label === inputValue) &&
    !selectedValues?.includes(inputValue)
      ? [
          { label: inputValue, value: inputValue },
          ...initialOptions.filter(opt => !selectedValues?.includes(opt.value)),
        ]
      : initialOptions.filter(opt => !selectedValues?.includes(opt.value))

  return (
    <Autocomplete
      freeSolo
      autoHighlight
      value={value}
      inputValue={inputValue}
      options={displayOptions}
      getOptionLabel={option => (typeof option === 'string' ? option : option.label)}
      renderOption={(props, option) => {
        return (
          <li {...props}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              {option.label}
            </span>
          </li>
        )
      }}
      renderInput={params => {
        return (
          <>
            <TextField
              {...params}
              label={label}
              error={error || isTooLong}
              helperText={isTooLong ? maxLengthErrorText : errorText}
              autoFocus={autoFocus}
              onBlur={onBlur}
              InputProps={{
                ...params.InputProps,
                startAdornment: <>{params.InputProps.startAdornment}</>,
              }}
            />
          </>
        )
      }}
      onInputChange={(_, newInputValue, reason) => {
        // Update input value based on user input
        if (reason === 'input') {
          setInputValue(newInputValue)
        }

        if (reason === 'clear') {
          setValue(null)
          setInputValue(INITIAL_FIELD_KEY)
          onChange?.(INITIAL_FIELD_KEY)
        }

        if (reason === 'reset') {
          setInputValue(value?.label || INITIAL_FIELD_KEY)
        }
      }}
      onChange={(_, newValue, reason) => {
        // Only trigger onChange when user clicks on an option
        if (reason === 'selectOption') {
          selectionMadeRef.current = true

          if (typeof newValue === 'string') {
            const newOption = { label: newValue, value: newValue }
            setValue(newOption)
            setInputValue(newValue)
            onChange?.(newValue)
          } else if (newValue) {
            setValue(newValue)
            setInputValue(newValue.label)
            onChange?.(newValue.value)
          }
        } else if (reason === 'clear') {
          setValue(null)
          setInputValue(INITIAL_FIELD_KEY)
          onChange?.(INITIAL_FIELD_KEY)
        }
      }}
      onClose={() => {
        // Reset to previous value if no selection was made
        if (!selectionMadeRef.current) {
          setInputValue(value?.label || INITIAL_FIELD_KEY)
        }
        selectionMadeRef.current = false
      }}
    />
  )
}
